import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useSnackbar } from "notistack";
import { ApiEndpoint } from "../../../types";
import {
  getStyleChoices,
  getStyleFieldKey,
  isIconStyled,
  baseGeometry,
  buildUpdatedStyle,
  buildUpdatedFieldSchema,
  CmStyleChoice
} from "../../../utilities/cmTableStyle";
import { updateCmTable } from "../../../services/cmTables";
import { useConfig } from "../../../contexts/ConfigContext";
import { useMapActions } from "../../../store/map";
import useAuth from "../../../hooks/useAuth";

/** Resolves an icon path ("icons/Camp.png" or absolute URL) to a usable <img> src. */
export function resolveIconUrl(icon?: string): string {
  if (!icon) return "";
  if (/^https?:\/\//i.test(icon)) return icon;
  const base = process.env.PUBLIC_URL || "";
  return `${base}/${icon.replace(/^\//, "")}`;
}

/** Curated set of map icons that ship in `public/icons/` for point tables. */
const AVAILABLE_MAP_ICONS = [
  "icons/Berries.png",
  "icons/Camp.png",
  "icons/Dugong.png",
  "icons/Spear_Boomerang_Wood.png",
  "icons/Stone_Artifacts.png",
  "icons/Text.png",
  "icons/Turtle.png",
  "icons/Waterhole.png"
];

/** A small palette of default colors offered for polygon/line styling. */
const DEFAULT_COLOR_PALETTE = [
  "#e74c3c",
  "#e67e22",
  "#f1c40f",
  "#2ecc71",
  "#1abc9c",
  "#3498db",
  "#9b59b6",
  "#34495e",
  "#95a5a6",
  "#000000"
];

interface SchemaField {
  name: string;
  type?: string;
  label?: string;
  choices?: string[];
}

interface CmTableRowDataFieldsProps {
  endpoint: ApiEndpoint;
  rowData: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  disabled?: boolean;
  /**
   * Called after the table's styling config is updated inline (add/edit choice),
   * so the parent form can refresh its own preview immediately. The `endpoint`
   * held by the form is captured in the entity-form store and does not react to
   * global config changes, hence this explicit hand-off.
   */
  onStyleConfigChange?: (nextStyle: Record<string, any>, nextFields: SchemaField[]) => void;
}

/** Small swatch shown beside a style choice — icon thumbnail (point) or color dot. */
function ChoiceSwatch({ choice, iconStyled }: { choice: CmStyleChoice; iconStyled: boolean }) {
  if (iconStyled) {
    return (
      <Box component='img' src={resolveIconUrl(choice.icon)} alt='' sx={{ width: 20, height: 20, objectFit: "contain", flexShrink: 0 }} />
    );
  }
  return <Box sx={{ width: 16, height: 16, borderRadius: "3px", bgcolor: choice.color || "#9ca3af", flexShrink: 0 }} />;
}

/**
 * Renders editable inputs for a CM-table row's `row_data`, driven by the
 * endpoint's `field_schema`. The single "styling" field (see cmTableStyle) is
 * rendered as a rich selector that previews each choice's icon (point) or color
 * (polygon/line); other fields render as selects (with `choices`) or text/number.
 */
export default function CmTableRowDataFields({ endpoint, rowData, onChange, disabled, onStyleConfigChange }: CmTableRowDataFieldsProps) {
  const spatialType = endpoint.spatialType;
  const style = endpoint.style;

  const { enqueueSnackbar } = useSnackbar();
  const { apiEndpoints, updateConfig } = useConfig();
  const { bumpCmTableLayerVersion } = useMapActions();
  const { canUpdate } = useAuth();

  const styleFieldKey = useMemo(() => getStyleFieldKey(style, spatialType), [style, spatialType]);
  const styleChoices = useMemo(() => getStyleChoices(style, spatialType), [style, spatialType]);
  const iconStyled = isIconStyled(spatialType);
  const geometrySupportsStyling = baseGeometry(spatialType) !== "none";

  const schemaFields = useMemo<SchemaField[]>(() => (endpoint.fields as SchemaField[]) ?? [], [endpoint.fields]);

  // The human-readable name of the styling field, taken from its field_schema `label`
  // (falling back to the field key). This drives the dropdown title, the add/edit
  // controls and messages so each table shows its own term (Type, Category, …).
  const styleLabel = useMemo(() => {
    const fromSchema = schemaFields.find((f) => f.name === styleFieldKey)?.label;
    const base = (fromSchema && fromSchema.trim()) || styleFieldKey || "Type";
    return base.charAt(0).toUpperCase() + base.slice(1);
  }, [schemaFields, styleFieldKey]);
  const [editing, setEditing] = useState<null | { value: string; output: string; isNew: boolean }>(null);
  const [saving, setSaving] = useState(false);

  const iconOptions = useMemo(() => {
    const fromChoices = styleChoices.map((c) => c.icon).filter((v): v is string => !!v);
    return Array.from(new Set([...AVAILABLE_MAP_ICONS, ...fromChoices]));
  }, [styleChoices]);

  const setField = (name: string, value: any) => {
    const next = { ...rowData };
    if (value === "" || value === undefined || value === null) delete next[name];
    else next[name] = value;
    onChange(next);
  };

  // Whether the styling field is already declared in field_schema (so we don't render it twice).
  const styleFieldInSchema = schemaFields.some((f) => f.name === styleFieldKey);
  const hasStyleSelector = styleChoices.length > 0 && (styleFieldInSchema || !!style);
  const canManageTypes = canUpdate && geometrySupportsStyling && !!endpoint.tableSlug;

  if (schemaFields.length === 0 && !hasStyleSelector && !canManageTypes) return null;

  const startAdd = () => setEditing({ value: "", output: iconStyled ? (iconOptions[0] ?? "") : DEFAULT_COLOR_PALETTE[0], isNew: true });
  const startEdit = (choice: CmStyleChoice) =>
    setEditing({ value: choice.value, output: (iconStyled ? choice.icon : choice.color) ?? "", isNew: false });
  const cancelEdit = () => setEditing(null);

  const confirmEdit = async () => {
    if (!editing || !endpoint.tableSlug) return;
    const value = editing.value.trim();
    if (!value) {
      enqueueSnackbar(`Please enter a ${styleLabel.toLowerCase()} name`, { variant: "warning" });
      return;
    }
    if (!editing.output) {
      enqueueSnackbar(iconStyled ? "Please select an icon" : "Please select a color", { variant: "warning" });
      return;
    }
    if (editing.isNew && styleChoices.some((c) => c.value === value)) {
      enqueueSnackbar(`"${value}" already exists`, { variant: "warning" });
      return;
    }

    // Build the next choice list (add or replace-by-value).
    const nextChoice: CmStyleChoice = iconStyled ? { value, icon: editing.output } : { value, color: editing.output };
    const nextChoices = editing.isNew ? [...styleChoices, nextChoice] : styleChoices.map((c) => (c.value === value ? nextChoice : c));

    const nextStyle = buildUpdatedStyle(style, spatialType, styleFieldKey, nextChoices);
    const nextFields = buildUpdatedFieldSchema(
      schemaFields as Array<{ name: string; type: string; label?: string; choices?: string[] }>,
      styleFieldKey,
      nextChoices.map((c) => c.value)
    );

    setSaving(true);
    try {
      await updateCmTable(endpoint.tableSlug, {
        field_schema: nextFields as Array<{ name: string; type: string; label?: string; choices?: string[] }>,
        map_display_config: { style: nextStyle }
      });

      // Reflect in global config so the live map + other views re-style.
      const updatedEndpoints = (apiEndpoints ?? []).map((ep) =>
        ep.tableSlug === endpoint.tableSlug || ep.tableName === endpoint.tableName
          ? ({ ...ep, style: nextStyle, fields: nextFields } as ApiEndpoint)
          : ep
      );
      updateConfig({ apiEndpoints: updatedEndpoints });
      bumpCmTableLayerVersion();

      // Hand the new style/fields to the parent form for instant preview.
      onStyleConfigChange?.(nextStyle, nextFields as SchemaField[]);

      enqueueSnackbar(editing.isNew ? `Added ${styleLabel.toLowerCase()} "${value}"` : `Updated ${styleLabel.toLowerCase()} "${value}"`, {
        variant: "success"
      });
      setEditing(null);
    } catch (err) {
      console.error("[CmTableRowDataFields] Failed to update table styling config:", err);
      enqueueSnackbar(`Failed to save ${styleLabel.toLowerCase()}. Please try again.`, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderStyleSelector = () => {
    const value = rowData[styleFieldKey] ?? "";
    const label = styleLabel;
    return (
      <FormControl key={styleFieldKey} fullWidth sx={{ gap: "0.37rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant='body1' color='grey.500'>
            {label}
          </Typography>
          {canManageTypes && !editing && (
            <Button
              size='small'
              startIcon={<AddRoundedIcon />}
              onClick={startAdd}
              disabled={disabled || saving}
              sx={{ textTransform: "none" }}
            >
              Add {styleLabel.toLowerCase()}
            </Button>
          )}
        </Box>
        <Select
          value={value}
          displayEmpty
          disabled={disabled}
          onChange={(e) => setField(styleFieldKey, e.target.value)}
          renderValue={(selected) => {
            if (!selected) return <Typography color='grey.400'>Default styling</Typography>;
            const choice = styleChoices.find((c) => c.value === selected);
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {choice && <ChoiceSwatch choice={choice} iconStyled={iconStyled} />}
                <span>{String(selected)}</span>
              </Box>
            );
          }}
          sx={{ "& fieldset": { borderColor: "grey.300", borderRadius: "0.5rem" } }}
        >
          <MenuItem value=''>
            <Typography color='grey.500'>Default styling</Typography>
          </MenuItem>
          {styleChoices.map((choice) => (
            <MenuItem key={choice.value} value={choice.value} sx={{ gap: "0.5rem" }}>
              <ChoiceSwatch choice={choice} iconStyled={iconStyled} />
              <ListItemText primary={choice.value} />
              {canManageTypes && (
                <Tooltip title='Edit icon/color'>
                  <IconButton
                    size='small'
                    edge='end'
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(choice);
                    }}
                  >
                    <EditRoundedIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderChoiceEditor = () => {
    if (!editing) return null;
    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: "0.5rem",
          p: "0.75rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
          <Typography variant='body2' color='grey.600'>
            {editing.isNew ? `Add new ${styleLabel.toLowerCase()}` : `Edit "${editing.value}"`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
            {saving ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Tooltip title='Confirm'>
                  <IconButton size='small' color='success' onClick={confirmEdit}>
                    <CheckRoundedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Cancel'>
                  <IconButton size='small' color='error' onClick={cancelEdit}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <TextField
          fullWidth
          size='small'
          label={`${styleLabel} name`}
          value={editing.value}
          disabled={!editing.isNew || saving}
          placeholder='e.g. Camp'
          onChange={(e) => setEditing((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
        />

        {iconStyled ? (
          <Box>
            <Typography variant='caption' color='grey.500' sx={{ display: "block", mb: "0.375rem" }}>
              Icon
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {iconOptions.map((icon) => {
                const selected = editing.output === icon;
                return (
                  <Box
                    key={icon}
                    role='button'
                    onClick={() => !saving && setEditing((prev) => (prev ? { ...prev, output: icon } : prev))}
                    sx={{
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "0.5rem",
                      cursor: saving ? "default" : "pointer",
                      border: "2px solid",
                      borderColor: selected ? "primary.main" : "grey.200",
                      bgcolor: selected ? "primary.50" : "transparent"
                    }}
                  >
                    <Box component='img' src={resolveIconUrl(icon)} alt='' sx={{ width: 28, height: 28, objectFit: "contain" }} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant='caption' color='grey.500' sx={{ display: "block", mb: "0.375rem" }}>
              Color
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
              {DEFAULT_COLOR_PALETTE.map((color) => {
                const selected = editing.output?.toLowerCase() === color.toLowerCase();
                return (
                  <Box
                    key={color}
                    role='button'
                    onClick={() => !saving && setEditing((prev) => (prev ? { ...prev, output: color } : prev))}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: color,
                      cursor: saving ? "default" : "pointer",
                      border: "2px solid",
                      borderColor: selected ? "primary.main" : "rgba(0,0,0,0.15)",
                      boxShadow: selected ? "0 0 0 2px rgba(25,118,210,0.25)" : "none"
                    }}
                  />
                );
              })}
              <Box
                component='input'
                type='color'
                value={/^#([0-9a-f]{6})$/i.test(editing.output) ? editing.output : "#888888"}
                disabled={saving}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditing((prev) => (prev ? { ...prev, output: e.target.value } : prev))
                }
                sx={{
                  width: 36,
                  height: 28,
                  p: 0,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: "0.25rem",
                  cursor: "pointer",
                  bgcolor: "transparent"
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {hasStyleSelector && renderStyleSelector()}
      {!hasStyleSelector && canManageTypes && !editing && (
        <Button
          size='small'
          startIcon={<AddRoundedIcon />}
          onClick={startAdd}
          disabled={disabled || saving}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Add {styleLabel.toLowerCase()}
        </Button>
      )}
      {renderChoiceEditor()}
    </Box>
  );
}
